import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DashboardService } from './dashboard';
import { AlertService } from './alert.service';
import { StorageService } from './storage.service';
import {
  GatewayDashboardFilters,
  OverviewCardsResponse,
  DetailsTableResponse,
  FilterOptionsResponse,
  GatewayReportField,
} from '../dashboard/gateway-dashboard/gateway-summary/gateway-summary.data';
import { ReportTypes, ReportVersion } from '../reports/models/reports';
import { DimensionOptionsResponse } from '../dashboard/gateway-dashboard/approval-analytics/approval-analytics.data';

const route = 'approval_analytics_dashboard';
// Placeholder fields value — required by the shared serializer on every endpoint
const placeHolderFields = ['day_of_week'];

@Injectable()
export class ApprovalAnalyticsDashboardService extends DashboardService {

  constructor(
    http: HttpClient,
    alertService: AlertService,
    storageService: StorageService,
  ) {
    super(http, alertService, storageService);
  }

  // ── Request builder ──────────────────────────────────────────────────────────

  buildRequest(filters: GatewayDashboardFilters, fields: string[], extra?: object): object {
    const timezone = this.getTimezone();
    const body: { [key: string]: any } = {
      report_version: ReportVersion.Clickhouse,
      report_type: ReportTypes.GATEWAY_TRANSACTION,
      date_range: {
        startDate: this.convertDateToUtc(filters.start_date, timezone),
        endDate: this.convertDateToUtc(filters.end_date, timezone),
      },
      timezone,
      accounts: this.getAccountIds(),
      fields,
    };

    if (filters.compare_start_date && filters.compare_end_date) {
      body['compare_date_range'] = {
        startDate: this.convertDateToUtc(filters.compare_start_date, timezone),
        endDate: this.convertDateToUtc(filters.compare_end_date, timezone),
      };
    }

    const apiFilters: { [key: string]: any } = {};
    if (filters.merchant_id) {
      apiFilters['gateway_id'] = Number(filters.merchant_id);
    }
    if (filters.campaign_id) {
      apiFilters['campaign_id'] = filters.campaign_id;
    }
    if (filters.issuer_bank_name && filters.issuer_bank_name.length) {
      apiFilters[GatewayReportField.IssuerBankName] = filters.issuer_bank_name;
    }
    if (filters.card_bin && filters.card_bin.length) {
      apiFilters[GatewayReportField.CardBin] = filters.card_bin;
    }
    if (Object.keys(apiFilters).length) {
      body['filters'] = apiFilters;
    }

    if (filters.order_type) {
      body['stage'] = filters.order_type;
    }

    if (filters.count_type) {
      body['count_type'] = filters.count_type;
    }

    if (extra) {
      Object.assign(body, extra);
    }

    return body;
  }

  // ── Panel methods ────────────────────────────────────────────────────────────

  getOverviewCards(filters: GatewayDashboardFilters): Observable<OverviewCardsResponse> {
    return this.http.post<OverviewCardsResponse>(
      route + '/overview_cards/',
      this.buildRequest(filters, placeHolderFields)
    );
  }

  getDetailsTable(
    filters: GatewayDashboardFilters,
    fields: string[],
    extra?: object,
  ): Observable<DetailsTableResponse> {
    return this.http.post<DetailsTableResponse>(route + '/details_table/', this.buildRequest(filters, fields, extra));
  }

  getDimensionOptions(filters: GatewayDashboardFilters): Observable<DimensionOptionsResponse> {
    return this.http.post<DimensionOptionsResponse>(
      route + '/dimension_options/',
      this.buildRequest(filters, placeHolderFields)
    );
  }

  getFilterOptions(filters: GatewayDashboardFilters): Observable<FilterOptionsResponse> {
    return this.http.post<FilterOptionsResponse>(
      route + '/filter_options/',
      this.buildRequest(filters, placeHolderFields)
    );
  }

}
